import { Injectable } from '@angular/core';
import { CodingFieldSdk } from '../sdks/coding-field.sdk';
import { BehaviorSubject, Observable } from 'rxjs';
import { EditableFieldSection } from '../models/ui/editable-field-section.model';
import { Collection } from '../models/ui/collection.model';
import { EditableFieldNode } from '../models/ui/editable-field-node.model';
import { LexbeCase } from '../models/case.model';
import { CaseSdk } from '../sdks/case.sdk';
import { ProcessingQueueService } from '../services/processing-queue.service';
import { CaseResource } from '../models/resource/case.resource';
import { CaseDashboardSdk } from '../sdks/case-dashboard.sdk';
import { CaseHubSdk } from '../sdks/case.hub-sdk';
import { CaseShards } from '../models/case-shards.model';
import { CaseShardsSdk } from '../sdks/case-shards.sdk';

export class ProcessingStepCount {
	functionName: string;
	stepsRemaining: number;
	progress: number;
	workflowKey?: string;
	workflowName?: string;
	workflowExecutionState?: "New" | "Created" | "Started" | "Active" | "OnHold" | "Complete" | "Error" | "Aborted";
}

@Injectable({
	providedIn: 'root'
})
export class CaseManager {
	public editableFieldCollection$: Observable<Collection<EditableFieldSection>>;
	public caseCollection$: Observable<LexbeCase[]>;
	public caseDashboardYears$ = this.caseDashboardSdk.getCaseDashboardYears();
	public processing$: Observable<ProcessingStepCount[]>;

	private editableFieldCollectionSource = new BehaviorSubject<Collection<EditableFieldSection>>(null);
	private caseCollectionSource: BehaviorSubject<LexbeCase[]> = new BehaviorSubject<LexbeCase[]>([]);

	constructor(
		private codingFieldsSdk: CodingFieldSdk,
		private caseSdk: CaseSdk,
		private processingService: ProcessingQueueService,
		private caseDashboardSdk: CaseDashboardSdk,
		private caseHubSdk: CaseHubSdk,
		private caseShardsSdk: CaseShardsSdk) {
		this.editableFieldCollection$ = this.editableFieldCollectionSource.asObservable();
		this.caseCollection$ = this.caseCollectionSource.asObservable();
		this.processing$ = caseHubSdk.processing$;
	}

	public refreshEditableFields(): void {
		this.codingFieldsSdk.get().subscribe((response) => {
			this.editableFieldCollectionSource.next(response);
		});
	}

	public saveEditableField(params: SaveEditableFieldParams): Observable<EditableFieldNode | EditableFieldSection> {
		return this.codingFieldsSdk.put({ field: params?.field });
	}

	public removeEditableField(params: RemoveEditableFieldParams): Observable<void> {
		return this.codingFieldsSdk.delete({ field: params?.field });
	}

	public importEditableFields(params: ImportEditableFieldParams): Observable<void> {
		return this.codingFieldsSdk.importCodingFields({ file: params?.file });
	}

	public refreshAdminCases(term: string, orderBy: [string, "ASC" | "DESC"]): void {
		this.getCases(term, orderBy).subscribe(data =>
			this.caseCollectionSource.next(data)
		);
	}

	public getCases(term: string, orderBy: [string, "ASC" | "DESC"]): Observable<LexbeCase[]> {
		return this.caseSdk.getCases(orderBy, term);
	}

	public getCasesByAccountId(accountId: string): Observable<LexbeCase[]> {
		return this.caseSdk.getCasesByAccountId(accountId);
	}

	public getCase(caseId: string): Observable<LexbeCase> {
		return this.caseSdk.getCaseDetails(caseId);
	}

	public updateCaseAccount(lexbeCase: LexbeCase): Observable<LexbeCase> {
		return this.caseSdk.put(lexbeCase);
	}

	public updateCase(caseId: string, deltaCase: Partial<CaseResource>): Observable<void> {
		return this.caseSdk.patchCase(caseId, deltaCase);
	}

	public deleteCase(caseId: string): Observable<void> {
		return this.caseSdk.deleteCase(caseId);
	}

	public getCaseDashboardData(year: string): Observable<Array<Array<string>>> {
		return this.caseDashboardSdk.getCaseDashboardData(year);
	}

	public getCaseShards(caseId: string): Observable<CaseShards> {
		return this.caseShardsSdk.get(caseId);
	}

	//TODO: REFACTOR THIS METHOD POST PROCESSING SCHEDULER MIGRATION
	public putCaseShards(caseId: string, primaryShardCount: number): Observable<void> {
		return this.caseShardsSdk.put(caseId, primaryShardCount);
	}
}

interface SaveEditableFieldParams {
	field: EditableFieldSection | EditableFieldNode;
}

interface RemoveEditableFieldParams {
	field: EditableFieldSection | EditableFieldNode;
}

interface ImportEditableFieldParams {
	type: 'document-fields';
	file: File;
}
